import React, { useState, useEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import SelectField from '../Select/Select'
import moment from 'moment'
import Calendar from 'react-calendar'
import { translate } from 'react-i18next'
import TextField from '../TextField'
import './DateIntervalInput.css'

let disableClose = false

const DateIntervalInput = ({
  t,
  label,
  onChange,
  required,
  initialValue,
  noSelect,
  minDate,
  maxDate,
  disableAutoClose,
  showHelperArea,
  validate = true,
  maxLimitDay = false,
  defaultLastMonth = false,
  filtersGlobal = false,
  mainRef,
  error, 
  errorMsg
}) => {

  const getValue = ({ value: optValue }) =>
    ({
      today: () => [moment(), moment()],
      yesterday: () => [
        moment().subtract(1, 'day'),
        moment().subtract(1, 'day'),
      ],
      'last-week': () => [moment().subtract(1, 'week'), moment()],
      'last-biweek': () => [moment().subtract(15, 'day'), moment()],
      'last-month': () => [moment().subtract(1, 'month'), moment()],
      custom: () => [moment(), moment()],
    }[optValue])

  const [value, setValue] = useState(defaultLastMonth ? getValue({value: 'last-month'}) : initialValue)
  const [isCalendarOpen, setCalendarOpen] = useState(false)
  const [periodOptions, setPeriodOptions] = useState([])

  useEffect(() => {
    if(filtersGlobal) {

      handleCustomChange(filtersGlobal)
    }
  }, [])
  useEffect(() => {
    const allOptions = [
      { value: 'today', label: t('dateintervalinput.today') },
      { value: 'yesterday', label: t('dateintervalinput.yesterday') },
      { value: 'last-week', label: t('dateintervalinput.lastWeek') },
      {
        value: 'last-biweek',
        label: t('dateintervalinput.lastBiweek'),
      },
      { value: 'last-month', label: t('dateintervalinput.lastMonth') },
      {
        value: 'custom',
        label: t('dateintervalinput.customInterval'),
        divider: true,
      },
    ]

    const primaryOptions = [
      {
        value: 'today',
        label: t('dateintervalinput.today')
      },
      {
        value: 'yesterday',
        label: t('dateintervalinput.yesterday')
      }
    ]

    if (maxLimitDay) {
      if (maxLimitDay >= 7) primaryOptions.push({
        value: 'last-week',
        label: t('dateintervalinput.lastWeek')
      })
      if (maxLimitDay >= 15) primaryOptions.push({
        value: 'last-biweek',
        label: t('dateintervalinput.lastBiweek'),
      })
      if (maxLimitDay >= 30) primaryOptions.push({
        value: 'last-month',
        label: t('dateintervalinput.lastMonth')
      })
      setPeriodOptions(primaryOptions)
    } else setPeriodOptions(allOptions)
  }, [])

  const ref = useRef(null)


  useEffect(() => {
    if (!disableAutoClose) {
      document.addEventListener('mousedown', handleInteraction)
      return () => {
        document.removeEventListener('mousedown', handleInteraction)
      }

    }
  }, [])

  useEffect(() => {
    if (initialValue && initialValue !== value) {
      setValue(initialValue)
    }
  }, [initialValue])

  const handleInteraction = (e) => {
    if (!disableClose && !ref.current.contains(e.target)) {
      setCalendarOpen(false)
    }
  }

  const handleChange = (opt) => {
    const currentValue = (getValue(opt) || (() => null))()
    setValue(currentValue)
    onChange && onChange(currentValue)
    if (opt.value === 'custom') {
      disableClose = true
      setCalendarOpen(true)
      setTimeout(() => {
        disableClose = false
      }, 500)
    }
  }
  const handleCustomChange = ([ini, end]) => {
    const value = [moment(ini), moment(end)]
    setValue(value)
    setCalendarOpen(false)
    onChange && onChange(value)
  }

  useEffect(() => {
    window.addEventListener('resize', () => setCalendarOpen(false))
    document.addEventListener('scroll', handleInteraction, true)

    return () => {
      window.removeEventListener('resize', () => setCalendarOpen(false))
      document.removeEventListener('scroll', handleInteraction, true)
    }
  }, [])

  const getHtmlValues = (element) => {
    let currentElement = element
    let top = element.offsetTop
    let left = element.offsetLeft
    const windowHeight = window.innerHeight

    while (currentElement.offsetParent) {
      currentElement = currentElement.offsetParent
      top += currentElement.offsetTop
      left += currentElement.offsetLeft
    }

    if (currentElement.className === 'filters fixed open')
      left -= currentElement.offsetLeft

    currentElement = element
    let scrollTop = 0

    while (currentElement.parentElement) {
      if (window.getComputedStyle(currentElement).position === 'fixed') break
      scrollTop += currentElement.scrollTop
      currentElement = currentElement.parentElement
    }
    scrollTop += currentElement.scrollTop

    const calculatedTopValue = top - (scrollTop + 10) + element.clientHeight
    const renderAbove = calculatedTopValue + 282 >= windowHeight

    const finalTopValue = renderAbove
      ? top - (scrollTop + 256)
      : calculatedTopValue

    return [finalTopValue, left]
  }

  const [top, left] = (ref.current && getHtmlValues(ref.current)) || [0, 0]

  return (
    <div
      className={`date-interval-input-wrapper ${isCalendarOpen ? 'calendar-open' : ''} ${error ? 'error' : ''}`}
      ref={ref}
    >
      {!noSelect && (
        <SelectField
          ref={mainRef}
          label={label}
          placeholder={!value && t('dateintervalinput.defaultPlaceholder')}
          {...(validate && {
            successText: t('dateintervalinput.valid'),
            errorText: t('dateintervalinput.invalid'),
            warningText: t('dateintervalinput.required'),
          })}
          showHelperArea={showHelperArea}
          required={required}
          value={value}
          disableSearch
          fill
          formatLabel={() =>
            value &&
            `${value[0].format('DD/MM/YYYY')} - ${value[1].format(
              'DD/MM/YYYY'
            )}`
          }
          validateValue={(currentValue) => Boolean(currentValue)}
          onChange={handleChange}
          options={periodOptions}
          onDropdownOpen={() => setCalendarOpen(false)}
          rightIcon='glyph glyph-calendar'
          {...(!maxLimitDay && {
            onRightIconClick: () => setCalendarOpen(!isCalendarOpen)
          })}
        />
      )}
      {noSelect && (
        <TextField
          placeholder={t('dateintervalinput.defaultPlaceholder')}
          label={label}
          required={required}
          fill
          showHelperArea={showHelperArea}
          onClick={() => setCalendarOpen(true)}
          controlledValue={
            value
              ? `${value[0].format('DD/MM/YYYY')} - ${value[1].format(
                'DD/MM/YYYY'
              )}`
              : ''
          }
          isControlledValue
          name={'date-interval-input'}
          rightIcon='glyph glyph-calendar'
          {...(!maxLimitDay && {
            onRightIconClick: () => setCalendarOpen(!isCalendarOpen)
          })}
          error={error} 
          errorMsg={errorMsg}
        />
      )}
      {isCalendarOpen && (
        <div className='daterange-title date-interval-input'>
          <div className='rangepicker' style={{ top, left }}>
            <Calendar
              calendarType='US'
              onChange={handleCustomChange}
              className='react-calendar'
              returnValue='range'
              selectRange
              defaultValue={
                initialValue
                  ? [initialValue[0].toDate(), initialValue[1].toDate()]
                  : false
              }
              minDate={minDate}
              maxDate={maxDate}
            />
          </div>
        </div>
      )}
    </div>
  )
}

DateIntervalInput.propTypes = {
  t: PropTypes.func,
  label: PropTypes.string,
  initialValue: PropTypes.array,
  onChange: PropTypes.func,
  required: PropTypes.bool,
  noSelect: PropTypes.bool,
  minDate: PropTypes.any,
  maxDate: PropTypes.any,
  disableAutoClose: PropTypes.bool,
  showHelperArea: PropTypes.bool,
  validate: PropTypes.bool,
  options: PropTypes.array,
  maxLimitDay: PropTypes.number,
  defaultLastMonth: PropTypes.bool,
  filtersGlobal: PropTypes.array,
  mainRef: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({ current: PropTypes.instanceOf(Element) })
  ]),
  error: PropTypes.string, 
  errorMsg:PropTypes.string
}

export default translate(null, { wait: true, translateFuncName: 't' })(
  DateIntervalInput
)
